import { TOKENS, BREAKPOINTS, type BreakpointName } from "./tokens.js";

/**
 * Typed access to a token as a CSS `var()` reference. Prefer referencing `var(--ac-*)` directly in
 * `*.module.scss`; this helper is for the rare case a value must be composed in TS (e.g. an
 * inline canvas fill or a Capacitor status-bar color). Passing an unregistered name throws — the JS-side
 * analogue of the Stylelint unknown-token firewall.
 */
export function token(name: `--${string}`): string {
  if (!(name in TOKENS)) {
    throw new Error(`Unknown design token "${name}" — not in @cntrl/tokens registry.`);
  }
  return `var(${name})`;
}

/** The raw literal value behind a token (not a `var()` reference) — for values that cannot be a var (e.g. a media-query px). */
export function tokenValue(name: `--${string}`): string {
  const value = TOKENS[name];
  if (value === undefined) {
    throw new Error(`Unknown design token "${name}" — not in @cntrl/tokens registry.`);
  }
  return value;
}

/** A `min-width` media query string for a breakpoint. Wall-clock parity with the MVP's collapses.*/
export function mediaUp(bp: BreakpointName): string {
  return `(min-width: ${BREAKPOINTS[bp]}px)`;
}

/** A `max-width` media query string (the breakpoint minus 0.02px, the standard exclusive-upper idiom). */
export function mediaDown(bp: BreakpointName): string {
  return `(max-width: ${BREAKPOINTS[bp] - 0.02}px)`;
}

/** Ergonomic grouped theme object (loosely typed): `theme.color["accent"]` → "var(--ac-color-accent)". */
export const theme = {
  color: groupVars("ac-color-"),
  font: groupVars("ac-font-"),
  fw: groupVars("ac-fw-"),
  fs: groupVars("ac-fs-"),
  space: groupVars("ac-space-"),
  radius: groupVars("ac-radius-"),
  shadow: groupVars("ac-shadow-"),
  z: groupVars("ac-z-"),
} as const;

function groupVars(prefix: string): Readonly<Record<string, string>> {
  const out: Record<string, string> = {};
  for (const full of Object.keys(TOKENS)) {
    const bare = full.slice(2); // drop leading "--"
    if (bare.startsWith(prefix)) out[bare.slice(prefix.length)] = `var(${full})`;
  }
  return Object.freeze(out);
}
